import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight, Scroll } from 'lucide-react';
import './../intro.css';

const TITLE_LETTERS = ['C', 'O', 'D', 'E', 'X'];
const SCENE_DURATIONS = {
  stars: 1300,
  scroll: 1500,
  seal: 1500,
  title: 1700,
}; // total ≈ 6.0s

const TOTAL = Object.values(SCENE_DURATIONS).reduce((a, b) => a + b, 0);

export default function CodexIntro({ onDone }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('stars'); // stars → scroll → seal → title → ready
  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  useEffect(() => {
    if (reducedMotion) {
      setPhase('ready');
      return;
    }
    const t1 = setTimeout(() => setPhase('scroll'), SCENE_DURATIONS.stars);
    const t2 = setTimeout(() => setPhase('seal'), SCENE_DURATIONS.stars + SCENE_DURATIONS.scroll);
    const t3 = setTimeout(() => setPhase('title'), SCENE_DURATIONS.stars + SCENE_DURATIONS.scroll + SCENE_DURATIONS.seal);
    const t4 = setTimeout(() => setPhase('ready'), TOTAL);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [reducedMotion]);

  const finish = () => {
    try { localStorage.setItem('codex_intro_seen', '1'); } catch { /* ignore */ }
    if (onDone) onDone();
    else navigate('/codex/dashboard', { replace: true });
  };

  const skip = () => finish();

  return (
    <div className="codex-intro" data-testid="codex-intro" role="dialog" aria-label="CODEX intro">
      {/* Stars */}
      <div className="codex-intro__stars" aria-hidden />

      {/* Aurora */}
      <div className="codex-intro__aurora" aria-hidden />

      {/* Skip button */}
      <button
        type="button"
        className="codex-intro__skip"
        onClick={skip}
        data-testid="codex-intro-skip"
        aria-label="Skip intro"
      >
        Skip ›
      </button>

      {/* Scroll falls in */}
      <AnimatePresence>
        {(phase === 'scroll' || phase === 'seal') && (
          <motion.div
            key="scroll"
            className="codex-intro__scroll"
            initial={{ y: '-130vh', rotate: -45, opacity: 0, scale: 0.7 }}
            animate={{
              y: 0,
              rotate: phase === 'seal' ? [0, -3, 3, 0] : 0,
              opacity: 1,
              scale: 1,
            }}
            exit={{ scale: 1.6, opacity: 0 }}
            transition={{
              y: { type: 'spring', damping: 14, mass: 0.9, duration: 1.1 },
              rotate: phase === 'seal'
                ? { duration: 0.35, repeat: 2, repeatType: 'mirror' }
                : { duration: 0.8, ease: 'easeOut' },
              opacity: { duration: 0.6 },
              scale: { duration: 0.6 },
            }}
          >
            <div className="codex-intro__scroll-disc" />
            <Scroll
              size={84}
              color="#1a0e2c"
              strokeWidth={2.4}
              style={{ position: 'absolute', filter: 'drop-shadow(0 0 8px #fff7d6)' }}
            />

            {/* Cracks emerging from the seal */}
            {phase === 'seal' && (
              <>
                {[-30, 0, 30].map((deg, i) => (
                  <motion.div
                    key={i}
                    className="codex-intro__crack"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 200, opacity: 1 }}
                    transition={{ delay: 0.2 + i * 0.12, duration: 0.45, ease: 'easeOut' }}
                    style={{
                      left: '50%',
                      bottom: '50%',
                      transform: `translateX(-50%) rotate(${deg}deg)`,
                    }}
                  />
                ))}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* CODEX Title — letter-by-letter reveal */}
      <AnimatePresence>
        {(phase === 'title' || phase === 'ready') && (
          <motion.div
            key="title"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            style={{ position: 'relative', zIndex: 5, textAlign: 'center', padding: '0 16px' }}
          >
            <div className="codex-intro__title" aria-label="CODEX">
              {TITLE_LETTERS.map((ch, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{ delay: 0.1 + i * 0.12, duration: 0.5 }}
                >
                  {ch}
                </motion.span>
              ))}
            </div>
            <motion.div
              className="codex-intro__subtitle"
              initial={{ opacity: 0, letterSpacing: '0.1em' }}
              animate={{ opacity: 1, letterSpacing: '0.5em' }}
              transition={{ delay: 0.95, duration: 0.7 }}
            >
              The Ancient Scrolls
            </motion.div>

            {phase === 'ready' && (
              <motion.div
                className="codex-intro__enter"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <button
                  type="button"
                  className="codex-btn codex-btn-primary"
                  onClick={finish}
                  data-testid="codex-intro-enter"
                  style={{ padding: '14px 26px' }}
                >
                  <Sparkles size={16} /> Enter the Codex <ChevronRight size={16} />
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
